'use client';

/**
 * ServicesCrossSell — productive cross-sell panel for the booking checkout.
 *
 * Hosts up to 3 transport services pulled from the live activities API
 * (filtered by category === 'transport' via useServices). Click "Agregar"
 * opens AddServiceModal in a fixed overlay; on confirm the payload is
 * mapped into a single-segment ServiceCartItem and persisted via the
 * unified cart's addServiceItem().
 *
 * Wired by CheckoutClient (Agent 2). No backend changes (Agent 3).
 *
 * Visual reference: app/proto/services-recommend/RecommendSection.tsx
 * Modal reference: app/proto/services-modal/Prototype.tsx
 *
 * NOTE: this is a different UX from the older ServiceRecommendation +
 * ServiceQuickAddModal flow (which embeds TransportBookingClient). Both
 * paths coexist; the new flow is for the compact cross-sell strip.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import Image from 'next/image';
import { useServices } from '@/lib/hooks/useServices';
import { useUnifiedCart } from '@/lib/unified-cart';
import type { Activity } from '@/lib/hooks/useActivities';
import AddServiceModal, {
  type AddServicePayload,
  type ServiceData,
} from './AddServiceModal';
import { calculateTransportTotalUSD } from '@/lib/transport-pricing';
import styles from './ServicesCrossSell.module.css';

// Activity is the public-facing shape; the transport-specific flat-rate
// fields are present at runtime via the admin schema but not on the type yet.
// Narrow locally instead of widening Activity globally to keep the surface
// area of this fix minimal.
interface TransportActivityShape {
  baseOccupancy?: number;
  extraGuestPrice?: { amount?: number };
  dailyPricing?: Record<string, number>;
}

const NUMBER_FORMAT_ES_CL = new Intl.NumberFormat('es-CL', { maximumFractionDigits: 0 });
const formatNumber = (n: number) => NUMBER_FORMAT_ES_CL.format(Math.round(n));

/**
 * Build the union of every date (ISO YYYY-MM-DD) covered by lodge stays and
 * activity bookings currently in the cart. Used as the `allowedDates` set in
 * AddServiceModal — services can only be scheduled within the user's existing
 * trip window. Returns [] when nothing relevant is in the cart.
 */
function isoDatesBetween(checkIn: string, checkOut: string): string[] {
  const out: string[] = [];
  const start = new Date(checkIn + 'T12:00:00');
  const end = new Date(checkOut + 'T12:00:00');
  const cursor = new Date(start);
  while (cursor <= end) {
    const y = cursor.getFullYear();
    const m = String(cursor.getMonth() + 1).padStart(2, '0');
    const d = String(cursor.getDate()).padStart(2, '0');
    out.push(`${y}-${m}-${d}`);
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}

interface ServicesCrossSellProps {
  /** Max services to surface (default 3). */
  limit?: number;
  /** Initially open the panel body (default true). */
  defaultExpanded?: boolean;
}

const PLACEHOLDER_IMG = '/images/placeholder-activity.webp';

/** Map an Activity (Firestore transport service) to the modal's ServiceData shape. */
function toServiceData(activity: Activity): ServiceData {
  const tagline =
    activity.subtitle ||
    activity.shortDescription ||
    activity.location?.name ||
    '';
  const transport = activity as Activity & TransportActivityShape;
  return {
    id: activity.id,
    name: activity.name,
    priceUSD: activity.basePrice?.amount ?? 0,
    tagline,
    img: activity.heroImage || activity.images?.[0] || PLACEHOLDER_IMG,
    baseOccupancy: transport.baseOccupancy,
    extraGuestPriceUSD: transport.extraGuestPrice?.amount,
    dailyPricing: transport.dailyPricing,
  };
}

// Feature flag — services cross-sell.
// Re-enabled 2026-05-19 after the soft-hide from 2026-05-18.
const SERVICES_CROSS_SELL_ENABLED = true;

export default function ServicesCrossSell({
  limit = 3,
  defaultExpanded = true,
}: ServicesCrossSellProps) {
  const { services, loading } = useServices();
  const { serviceItems, lodgeItems, activityItems, addServiceItem } = useUnifiedCart();

  /* Allowed date set = lodge stay range + activity dates. Empty when cart is empty (no restriction). */
  const allowedDates = useMemo(() => {
    const set = new Set<string>();
    for (const lodge of lodgeItems) {
      for (const iso of isoDatesBetween(lodge.checkIn, lodge.checkOut)) set.add(iso);
    }
    for (const act of activityItems) set.add(act.date);
    return Array.from(set).sort();
  }, [lodgeItems, activityItems]);

  const [expanded, setExpanded] = useState(defaultExpanded);
  const [openService, setOpenService] = useState<Activity | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  /* Slice + sort by price ascending — cheap first, most accessible upsell. */
  const visibleServices = useMemo(() => {
    const sorted = [...services].sort(
      (a, b) => (a.basePrice?.amount ?? 0) - (b.basePrice?.amount ?? 0),
    );
    return sorted.slice(0, limit);
  }, [services, limit]);

  /* activityId → first segment (date + time) so the added row can show the schedule. */
  const addedMap = useMemo(() => {
    const map = new Map<string, { date: string; time: string }>();
    for (const item of serviceItems) {
      const seg = item.segments[0];
      if (seg) map.set(item.activityId, { date: seg.date, time: seg.time });
    }
    return map;
  }, [serviceItems]);

  /* Lock body scroll while modal open. */
  useEffect(() => {
    if (!openService) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [openService]);

  /* Auto-dismiss toast (3.2s, matches CSS animation). */
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3200);
    return () => clearTimeout(t);
  }, [toast]);

  const handleClose = useCallback(() => {
    setOpenService(null);
  }, []);

  const handleConfirm = useCallback(
    (payload: AddServicePayload) => {
      // Find the activity by id so we have slug / image etc.
      const activity = visibleServices.find((s) => s.id === payload.service_id);
      if (!activity) {
        // Defensive — should never happen because user just clicked this row.
        console.error('[ServicesCrossSell] confirm payload for unknown service id', payload.service_id);
        return;
      }

      // Map the modal payload into the cart's segments[] shape.
      // We persist country/timezone metadata in extraFieldValues so the email
      // and the floating-cart display can show them without schema churn.
      //
      // CRITICAL: the cart subtotal MUST mirror the backend's flat-rate model
      // (functions/src/utils/pricing.ts calculateTransportSegmentPrice). Using
      // price_usd × pax here was the source of Stripe's amount-mismatch 400 at
      // /api/payments/stripe/create-intent — the booking persisted by the
      // server (flat per segment) diverged from grandTotalUSD computed here.
      // For round-trip (2 segments), calculateTransportTotalUSD sums both legs.
      const transport = activity as Activity & TransportActivityShape;
      const outbound = payload.segments[0];
      const subtotalUSD = calculateTransportTotalUSD(
        {
          basePriceAmount: payload.price_usd,
          baseOccupancy: transport.baseOccupancy,
          extraGuestAmount: transport.extraGuestPrice?.amount,
          dailyPricing: transport.dailyPricing,
        },
        payload.segments.map((s) => ({ date: s.date })),
        payload.pax,
      );

      const isRoundTrip = payload.segments.length > 1;
      addServiceItem({
        itemType: 'service',
        activityId: activity.id,
        activitySlug: activity.slug || activity.id,
        activityName: activity.name,
        activityImage: activity.heroImage || activity.images?.[0] || PLACEHOLDER_IMG,
        segments: payload.segments,
        persons: payload.pax,
        extraFieldValues: {
          user_country: payload.user_country,
          user_country_code: payload.user_country_code,
          time_chile_label: payload.time_chile_label,
          time_user_local: payload.time_user_local,
          time_user_label: payload.time_user_label,
          time_user_day_delta: String(payload.time_user_day_delta),
          // Google Places resolved pickup. `_place_id` will be empty
          // string in fallback mode (no NEXT_PUBLIC_GOOGLE_MAPS_API_KEY).
          // Drop-off is always the booked lodge — not collected here.
          pickup_address: payload.pickup_address,
          pickup_place_id: payload.pickup_place_id,
          ...(isRoundTrip && {
            trip_kind: 'round_trip',
            return_time_chile_label: payload.return_time_chile_label ?? '',
            return_time_user_local: payload.return_time_user_local ?? '',
            return_time_user_label: payload.return_time_user_label ?? '',
            return_time_user_day_delta: String(payload.return_time_user_day_delta ?? 0),
            return_dropoff_address: payload.return_dropoff_address ?? '',
            return_dropoff_place_id: payload.return_dropoff_place_id ?? '',
          }),
        },
        clientTimezone: 'America/Santiago',
        subtotalUSD,
      });

      setToast(`${payload.service} agregado · ${outbound.date} · ${outbound.time} CLT${isRoundTrip ? ' (ida y vuelta)' : ''}`);
      setOpenService(null);
    },
    [visibleServices, addServiceItem],
  );

  // Don't render the panel at all if there are no services available.
  if (!loading && visibleServices.length === 0) return null;

  // Feature flag — hidden in production until services launch.
  if (!SERVICES_CROSS_SELL_ENABLED) return null;

  return (
    <>
      <div className={`${styles.panel} ${expanded ? styles.panelOpen : ''}`}>
        <button
          className={styles.panelHeader}
          onClick={() => setExpanded((v) => !v)}
          type="button"
          aria-expanded={expanded}
        >
          <span className={styles.panelIcon}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M5 17h14M5 17l1-7a2 2 0 012-2h8a2 2 0 012 2l1 7M9 8V6a3 3 0 016 0v2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span className={styles.panelText}>
            <span className={styles.panelLabel}>Add a service</span>
            <span className={styles.panelHint}>
              Traslados y chofer · {loading ? '…' : visibleServices.length} disponibles
            </span>
          </span>
          {addedMap.size > 0 && (
            <span className={styles.panelBadge}>
              {addedMap.size} agregado{addedMap.size > 1 ? 's' : ''}
            </span>
          )}
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className={`${styles.panelChevron} ${expanded ? styles.panelChevronOpen : ''}`}
          >
            <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <div className={`${styles.panelBody} ${expanded ? styles.panelBodyOpen : ''}`}>
          {loading ? (
            <div className={styles.serviceLoading}>Cargando servicios…</div>
          ) : visibleServices.length === 0 ? (
            <div className={styles.serviceEmpty}>No hay servicios disponibles.</div>
          ) : (
            <div className={styles.serviceList}>
              {visibleServices.map((activity) => {
                const addedSchedule = addedMap.get(activity.id);
                const added = Boolean(addedSchedule);
                const sd = toServiceData(activity);
                return (
                  <div
                    key={activity.id}
                    className={`${styles.serviceRow} ${added ? styles.serviceRowAdded : ''}`}
                  >
                    <div className={styles.serviceThumb}>
                      <Image
                        src={sd.img}
                        alt=""
                        fill
                        sizes="38px"
                        style={{ objectFit: 'cover' }}
                        unoptimized
                      />
                    </div>
                    <div className={styles.serviceBody}>
                      <span className={styles.serviceName}>{sd.name}</span>
                      <span className={styles.serviceTagline}>
                        {addedSchedule
                          ? `${addedSchedule.date} · ${addedSchedule.time} CLT`
                          : sd.tagline}
                      </span>
                    </div>
                    <div className={styles.servicePrice}>
                      <span className={styles.servicePriceLabel}>desde</span>
                      <span className={styles.servicePriceValue}>US$ {formatNumber(sd.priceUSD)}</span>
                    </div>
                    <button
                      type="button"
                      className={`${styles.serviceAddBtn} ${added ? styles.serviceAddBtnAdded : ''}`}
                      onClick={() => setOpenService(activity)}
                      aria-label={added ? `Edit ${sd.name}` : `Add ${sd.name}`}
                    >
                      {added ? (
                        <>
                          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <span>Agendado</span>
                        </>
                      ) : (
                        <>
                          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                          </svg>
                          <span>Agregar</span>
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modal overlay */}
      {openService && (
        <div
          className={styles.overlay}
          onClick={handleClose}
          role="presentation"
        >
          <div
            className={styles.overlayInner}
            onClick={(e) => e.stopPropagation()}
          >
            <AddServiceModal
              service={toServiceData(openService)}
              onClose={handleClose}
              onConfirm={handleConfirm}
            />
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={styles.toast} role="status" aria-live="polite">
          <span className={styles.toastIcon}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span className={styles.toastText}>{toast}</span>
        </div>
      )}
    </>
  );
}
